import {
  CollectEnhancedInfoType,
  GuestQuestionType,
} from "@shared/modules/basket/types/common/serializer-types";
import { TicketItemGroupType } from "@shared/modules/item-groups/types/ticket-item-group-type";
import { CustomerInfoType } from "@shared/modules/customers/components/CustomerInfoForm/CustomerInfoForm.props";
import { logAppInfo, recordAppError } from "@shared/utils/errors/errorLogger";

export const addressKeysList = [
  "info_home_address_city",
  "info_home_address_country",
  "info_home_address_postal",
  "info_home_address_province",
  "info_home_address_street_name",
  "info_home_address_unit_number",
];

export const customerKeysList = [
  "info_email",
  "info_first_name",
  "info_last_name",
  "info_phone_number",
];

export const generateInitialValuesForCustomerForm = (
  data: Record<string, string>,
  itemGroups: TicketItemGroupType[]
): CustomerInfoType[] => {
  const initialValues: CustomerInfoType[] = [];

  const formatValues = (
    source: Record<string, string> | TicketItemGroupType,
    backup?: Record<string, string>
  ) => ({
    email: source.info_email || backup?.info_email || backup?.email || "",
    firstName:
      source.info_first_name ||
      backup?.info_first_name ||
      backup?.name_on_ticket?.split(" ")?.[0] ||
      "",
    lastName:
      source.info_last_name ||
      backup?.info_last_name ||
      backup?.name_on_ticket?.split(" ")?.[1] ||
      "",
    phone: source.info_phone_number || backup?.info_phone_number || "",
    name:
      `${source.info_first_name || ""} ${source.info_last_name || ""}`.trim() ||
      `${backup?.info_first_name || ""} ${backup?.info_last_name || ""}`.trim(),
  });

  if (itemGroups.length === 1) {
    const itemGroup = itemGroups[0];
    // If there are item groups, also pass basket as backup
    initialValues.push(formatValues(itemGroup, data));
  } else {
    // For each itemGroup, map the values
    itemGroups.forEach((itemGroup) => {
      initialValues.push(formatValues(itemGroup));
    });
  }
  return initialValues;
};

export const generateInitialValuesForEnhancedForm = (
  data: Record<string, string>,
  itemGroups: TicketItemGroupType[],
  getEnhancedInfoQuestions: (
    infoObj?: CollectEnhancedInfoType | undefined
  ) => GuestQuestionType[],
  hasMultiGuestItemGroups: boolean
) => {
  const initialValues: Record<string, string>[] = [];

  const formatValues = (
    enhancedInfo: GuestQuestionType[],
    source: Record<string, string> | TicketItemGroupType
  ) => {
    const info: Record<string, string> = {};
    enhancedInfo.forEach((question, index) => {
      const key = hasMultiGuestItemGroups
        ? `${source.id}-enhanced-info-questions-form-${index}`
        : `enhanced-info-questions-form-${index}`;
      info[key] = (source as Record<string, string>)[question.id];
    });
    return info;
  };

  if (hasMultiGuestItemGroups) {
    itemGroups.forEach((itemGroup) => {
      const igEnhancedInfo = { ...itemGroup.collect_info_fields };
      initialValues.push(
        formatValues(getEnhancedInfoQuestions(igEnhancedInfo), itemGroup)
      );
    });
  } else {
    initialValues.push(formatValues(getEnhancedInfoQuestions(), data));
  }

  return initialValues;
};

export const generateInitialValuesForAddressForm = (
  data: Record<string, string>,
  itemGroups: TicketItemGroupType[],
  hasMultiGuestItemGroups: boolean
) => {
  const initialValues: Record<string, string>[] = [];

  const formatValues = (
    source: Record<string, string> | TicketItemGroupType
  ) => {
    return addressKeysList.reduce((acc, field) => {
      const key = hasMultiGuestItemGroups
        ? `${source.id}-search-address-form_${field}`
        : `search-address-form_${field}`;
      acc[key] = (source as Record<string, string>)[field];
      return acc;
    }, {} as Record<string, string>);
  };

  if (hasMultiGuestItemGroups) {
    itemGroups.forEach((itemGroup) => {
      initialValues.push(formatValues(itemGroup));
    });
  } else {
    initialValues.push(formatValues(data));
  }
  return initialValues;
};

export const generateInitialValuesForCustomQuestionsForm = (
  itemGroups: TicketItemGroupType[],
  getCustomQuestions: (
    customQuestionsData?: GuestQuestionType[] | undefined
  ) => GuestQuestionType[],
  hasMultiGuestItemGroups: boolean
) => {
  const initialValues: Record<string, string>[] = [];

  const formatValues = (
    customQuestionsInfo: GuestQuestionType[],
    source?: TicketItemGroupType
  ) => {
    const info: Record<string, string> = {};
    customQuestionsInfo.forEach((question, index) => {
      const key = hasMultiGuestItemGroups
        ? `${source?.id}-custom-questions-form-${index}`
        : `custom-questions-form-${index}`;

      const value = question.response;

      let stringValue = "";
      if (value != null) {
        if (typeof value === "object") {
          try {
            stringValue = JSON.stringify(value);
          } catch (e) {
            logAppInfo("Formatting custom questions");
            recordAppError(new Error(`Invalid object for ${key}, skipping`));
          }
        } else {
          stringValue = String(value);
        }
      }
      info[key] = stringValue;
    });
    return info;
  };

  if (hasMultiGuestItemGroups) {
    itemGroups.forEach((itemGroup) => {
      const igCustomQuestions = itemGroup.info_custom_question_data ?? [];
      initialValues.push(
        formatValues(getCustomQuestions(igCustomQuestions), itemGroup)
      );
    });
  } else {
    initialValues.push(formatValues(getCustomQuestions()));
  }

  return initialValues;
};
